import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Sparkle, ArrowRight, ArrowLeft, CheckCircle, ArrowsClockwise,
  Buildings, Target, Palette, Microphone, X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ['E-commerce', 'SaaS', 'Service Business', 'Personal Brand', 'Fashion', 'Beauty & Skincare', 'Health & Wellness', 'Tech', 'Food & Beverage', 'Education', 'Finance', 'Real Estate'];
const TONES = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Educational', 'Bold', 'Friendly', 'Luxurious'];
const GOALS = ['Increase Sales', 'Build Brand Awareness', 'Generate Leads', 'Grow Social Following', 'Drive Website Traffic', 'Launch Products', 'Build Community'];

const SCREENS = [
  { id: 'A', label: 'Business' },
  { id: 'B', label: 'Goals' },
  { id: 'C', label: 'Brand look' },
];

/* ── Progress bar ── */
const ProgressBar = ({ current }) => (
  <div className="mb-8" data-testid="onboarding-progress">
    <div className="flex items-center justify-between mb-3">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#c8f060' }}>
        Step {current + 1} of {SCREENS.length}
      </p>
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: 'rgba(245,240,232,0.35)' }}>
        {SCREENS[current]?.label}
      </p>
    </div>
    <div className="flex gap-1.5">
      {SCREENS.map((_, i) => (
        <div key={i} className="flex-1 h-1 rounded-full transition-all duration-500"
          style={{ background: i <= current ? '#c8f060' : 'rgba(255,255,255,0.07)' }} />
      ))}
    </div>
  </div>
);

/* ── Inputs ── */
const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-[12px] mb-1.5" style={{ color: 'rgba(245,240,232,0.55)' }}>
      {label}{required && <span style={{ color: '#c8f060' }}> *</span>}
    </label>
    {children}
    {hint && <p className="text-[10.5px] mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{hint}</p>}
  </div>
);

const TextInput = (props) => (
  <input {...props}
    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
);

const TextArea = (props) => (
  <textarea {...props}
    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30 resize-none" />
);

const Pill = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
    style={{
      background: active ? 'rgba(200,240,96,0.15)' : 'rgba(255,255,255,0.05)',
      color: active ? '#c8f060' : 'rgba(245,240,232,0.55)',
      border: `1px solid ${active ? 'rgba(200,240,96,0.3)' : 'rgba(255,255,255,0.07)'}`,
    }}>
    {children}
  </button>
);

const PrimaryBtn = ({ onClick, disabled, children, testId }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className="w-full py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-40 hover:scale-[1.01]"
    style={{ background: '#c8f060', color: '#080808' }}>
    {children}
  </button>
);

const GhostBtn = ({ onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="w-full py-2.5 text-xs font-medium transition-all"
    style={{ color: 'rgba(245,240,232,0.4)' }}>
    {children}
  </button>
);

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [finalScreen, setFinalScreen] = useState(false);
  const [analyzingVoice, setAnalyzingVoice] = useState(false);

  // Pull a URL the user dropped in the landing-page Launch Kit teaser (if any)
  // so the onboarding "Website" field is pre-filled.
  const seedWebsite = (() => {
    try {
      const u = sessionStorage.getItem('scl_launch_kit_url');
      if (!u) return '';
      const clean = u.trim();
      if (!clean) return '';
      return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
    } catch { return ''; }
  })();

  const [brand, setBrand] = useState({
    brand_name: '', industry: '', description: '', target_audience: '',
    tone_of_voice: '', main_goals: [],
    website: seedWebsite, instagram: '', tiktok: '', facebook: '', linkedin: '',
    brand_colors: { primary: '#c8f060', secondary: '#080808', accent: '#22d3ee' },
    brand_fonts: { heading: 'Instrument Serif', body: 'Inter' },
    logo_url: '',
  });
  const [voiceSamples, setVoiceSamples] = useState(['']);

  const canA = brand.brand_name.trim() && brand.industry;
  const toggleGoal = (g) => setBrand(p => ({ ...p, main_goals: p.main_goals.includes(g) ? p.main_goals.filter(x => x !== g) : [...p.main_goals, g] }));
  const setColor = (k, v) => setBrand(p => ({ ...p, brand_colors: { ...p.brand_colors, [k]: v } }));

  const saveAndAdvance = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, brand);
      setStep(s => s + 1);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const analyzeVoiceIfAny = async () => {
    const samples = voiceSamples.filter(s => s.trim().length > 20);
    if (samples.length === 0) return null;
    try {
      const { data } = await axios.post(`${API_URL}/api/brand/voice-analyze`, { content_samples: samples });
      return data.voice_dna || null;
    } catch { return null; }
  };

  const finishSetup = async (skipped = false) => {
    setSaving(true);
    try {
      let voice_dna = null;
      if (!skipped) {
        voice_dna = await analyzeVoiceIfAny();
        await axios.put(`${API_URL}/api/user/profile`, voice_dna ? { ...brand, voice_dna } : brand);
      }
      // Kick off background plan gen — non-blocking
      axios.post(`${API_URL}/api/daily-plan/generate`, {
        business_type: brand.industry || 'Online Business',
        industry: brand.industry || 'General',
        goals: brand.main_goals.length > 0 ? brand.main_goals : ['engagement'],
        platforms: ['Instagram'],
        target_audience: brand.target_audience || '',
        tone: brand.tone_of_voice || 'Professional',
        priorities: '',
      }).catch(() => {});
      await axios.post(`${API_URL}/api/onboarding/complete`).catch(() => {});
      await axios.post(`${API_URL}/api/streak/checkin`).catch(() => {});
      setFinalScreen(true);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleAnalyze = async () => {
    setAnalyzingVoice(true);
    await finishSetup(false);
    setAnalyzingVoice(false);
  };

  /* ───────── Final transition screen ───────── */
  if (finalScreen) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(16px)' }} data-testid="onboarding-final">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
          className="w-full max-w-md mx-4 p-8 sm:p-10 rounded-2xl text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #141414 0%, #0a0a0a 100%)',
            border: '1px solid rgba(200,240,96,0.2)',
          }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,240,96,0.6), transparent)' }} />
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-96 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(200,240,96,0.18), transparent 70%)', filter: 'blur(20px)' }} />

          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5 relative"
            style={{ background: 'rgba(200,240,96,0.12)', border: '1px solid rgba(200,240,96,0.3)' }}>
            <CheckCircle size={32} weight="fill" style={{ color: '#c8f060' }} />
          </motion.div>

          <h2 className="text-2xl sm:text-3xl font-bold mb-3 relative" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', letterSpacing: '-0.01em' }}>
            Your marketing team is ready.
          </h2>
          <p className="text-sm mb-7 relative" style={{ color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>
            We&rsquo;ve built your first daily plan. Let&rsquo;s go.
          </p>
          <PrimaryBtn onClick={() => { try { localStorage.setItem('scl-show-mini-preview', '1'); } catch {} onComplete?.(); }} testId="onboarding-see-plan">
            See my plan <ArrowRight size={16} weight="bold" />
          </PrimaryBtn>
        </motion.div>
      </div>
    );
  }

  /* ───────── 3-step wizard ───────── */
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto py-8" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
        className="w-full max-w-lg mx-4 rounded-2xl my-auto"
        style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)', maxHeight: 'calc(100vh - 64px)' }}
        data-testid="onboarding-wizard">
        <div className="p-5 sm:p-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 96px)' }}>

          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Sparkle size={16} style={{ color: '#c8f060' }} weight="fill" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#c8f060' }}>Welcome to ScalitOS</span>
            </div>
          </div>

          <ProgressBar current={step} />

          <AnimatePresence mode="wait">
            {/* ═════════ Screen A — Business ═════════ */}
            {step === 0 && (
              <motion.div key="A" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-medium mb-1" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', letterSpacing: '-0.01em' }}>
                    First, let&rsquo;s get to know your business.
                  </h2>
                  <p className="text-[12.5px]" style={{ color: 'rgba(245,240,232,0.45)', lineHeight: 1.6 }}>
                    This helps us write content that sounds like you, not like everyone else.
                  </p>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-xl mb-1" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.08)' }}>
                  <Buildings size={18} style={{ color: '#c8f060' }} />
                  <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.55)' }}>Takes about 60 seconds.</p>
                </div>

                <Field label="Business / Brand Name" required>
                  <TextInput value={brand.brand_name} onChange={e => setBrand(p => ({ ...p, brand_name: e.target.value }))}
                    placeholder="Your brand or business name" data-testid="onboard-brand-name" />
                </Field>

                <Field label="Industry" required>
                  <div className="flex flex-wrap gap-2">
                    {INDUSTRIES.map(ind => (
                      <Pill key={ind} active={brand.industry === ind} onClick={() => setBrand(p => ({ ...p, industry: ind }))}>{ind}</Pill>
                    ))}
                  </div>
                </Field>

                <Field label="Brand Description" hint="What do you sell or offer? Who is it for?">
                  <TextArea value={brand.description} onChange={e => setBrand(p => ({ ...p, description: e.target.value }))}
                    placeholder="e.g. Handcrafted scented candles for cozy minimalist homes" rows={2} data-testid="onboard-description" />
                </Field>

                <Field label="Target Audience">
                  <TextInput value={brand.target_audience} onChange={e => setBrand(p => ({ ...p, target_audience: e.target.value }))}
                    placeholder="e.g. Women 25-35, design lovers, gift shoppers" data-testid="onboard-audience" />
                </Field>

                <div className="pt-2 space-y-2">
                  <PrimaryBtn onClick={saveAndAdvance} disabled={!canA || saving} testId="onboard-next-A">
                    {saving ? <><ArrowsClockwise size={16} className="animate-spin" /> Saving</> : <>Next <ArrowRight size={16} weight="bold" /></>}
                  </PrimaryBtn>
                </div>
              </motion.div>
            )}

            {/* ═════════ Screen B — Goals ═════════ */}
            {step === 1 && (
              <motion.div key="B" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-medium mb-1" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', letterSpacing: '-0.01em' }}>
                    What are you trying to grow?
                  </h2>
                  <p className="text-[12.5px]" style={{ color: 'rgba(245,240,232,0.45)', lineHeight: 1.6 }}>
                    Pick everything that applies. We&rsquo;ll focus your daily plan around these.
                  </p>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-xl mb-1" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.08)' }}>
                  <Target size={18} style={{ color: '#c8f060' }} weight="duotone" />
                  <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.55)' }}>Tap as many as fit. You can change these anytime.</p>
                </div>

                <Field label="Main Goals" hint="Select all that apply">
                  <div className="flex flex-wrap gap-2">
                    {GOALS.map(g => (
                      <Pill key={g} active={brand.main_goals.includes(g)} onClick={() => toggleGoal(g)} testId={`onboard-goal-${g.toLowerCase().replace(/\s+/g, '-')}`}>{g}</Pill>
                    ))}
                  </div>
                </Field>

                <Field label="Tone of Voice" hint="How do you want your content to sound?">
                  <div className="flex flex-wrap gap-2">
                    {TONES.map(t => (
                      <Pill key={t} active={brand.tone_of_voice === t} onClick={() => setBrand(p => ({ ...p, tone_of_voice: t }))} testId={`onboard-tone-${t.toLowerCase()}`}>{t}</Pill>
                    ))}
                  </div>
                </Field>

                <div className="pt-2 flex gap-3">
                  <button onClick={() => setStep(0)} data-testid="onboard-back-B"
                    className="px-4 py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all"
                    style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.55)' }}>
                    <ArrowLeft size={14} weight="bold" /> Back
                  </button>
                  <div className="flex-1">
                    <PrimaryBtn onClick={saveAndAdvance} disabled={saving} testId="onboard-next-B">
                      {saving ? <><ArrowsClockwise size={16} className="animate-spin" /> Saving</> : <>Next <ArrowRight size={16} weight="bold" /></>}
                    </PrimaryBtn>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ═════════ Screen C — Brand look (optional) ═════════ */}
            {step === 2 && (
              <motion.div key="C" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl sm:text-2xl font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8', letterSpacing: '-0.01em' }}>
                      Make your content look on-brand.
                    </h2>
                    <span className="text-[9px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>Optional</span>
                  </div>
                  <p className="text-[12.5px]" style={{ color: 'rgba(245,240,232,0.45)', lineHeight: 1.6 }}>
                    Skip for now and add later in Settings, or set it up in 60 seconds.
                  </p>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-xl mb-1" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.08)' }}>
                  <Palette size={18} style={{ color: '#c8f060' }} weight="duotone" />
                  <p className="text-[12px]" style={{ color: 'rgba(245,240,232,0.55)' }}>Adds your colours, fonts, and voice to every output automatically.</p>
                </div>

                {/* Brand colours */}
                <Field label="Brand Colours">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'primary', label: 'Primary' },
                      { key: 'secondary', label: 'Secondary' },
                      { key: 'accent', label: 'Accent' },
                    ].map(c => (
                      <div key={c.key} className="rounded-xl p-2 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <input type="color" value={brand.brand_colors[c.key] || '#000000'}
                          onChange={e => setColor(c.key, e.target.value)}
                          data-testid={`onboard-color-${c.key}`}
                          className="w-7 h-7 rounded cursor-pointer" style={{ border: 'none', background: 'transparent' }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.35)' }}>{c.label}</p>
                          <p className="text-[10.5px] font-mono truncate" style={{ color: '#f5f0e8' }}>{brand.brand_colors[c.key]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>

                {/* Fonts */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Heading Font">
                    <TextInput value={brand.brand_fonts.heading}
                      onChange={e => setBrand(p => ({ ...p, brand_fonts: { ...p.brand_fonts, heading: e.target.value } }))}
                      placeholder="e.g. Playfair Display" data-testid="onboard-font-heading" />
                  </Field>
                  <Field label="Body Font">
                    <TextInput value={brand.brand_fonts.body}
                      onChange={e => setBrand(p => ({ ...p, brand_fonts: { ...p.brand_fonts, body: e.target.value } }))}
                      placeholder="e.g. Inter" data-testid="onboard-font-body" />
                  </Field>
                </div>

                {/* Logo */}
                <Field label="Logo URL" hint="Direct link to a PNG or SVG. We&rsquo;ll use it in all your assets.">
                  <TextInput value={brand.logo_url}
                    onChange={e => setBrand(p => ({ ...p, logo_url: e.target.value }))}
                    placeholder="https://yoursite.com/logo.png" data-testid="onboard-logo-url" />
                </Field>

                {/* Voice DNA samples */}
                <Field label="Brand Voice Samples" hint="Paste your best content so ScalitOS writes in your voice.">
                  {voiceSamples.map((sample, i) => (
                    <textarea key={i} value={sample}
                      onChange={e => { const s = [...voiceSamples]; s[i] = e.target.value; setVoiceSamples(s); }}
                      placeholder="Paste a caption, email, or any brand content..."
                      rows={2}
                      data-testid={`onboard-voice-sample-${i}`}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30 resize-none mb-2" />
                  ))}
                  {voiceSamples.length < 3 && (
                    <button onClick={() => setVoiceSamples(p => [...p, ''])}
                      className="text-[11px] flex items-center gap-1 transition-colors" style={{ color: '#c8f060' }}
                      data-testid="onboard-add-sample">
                      + Add another sample
                    </button>
                  )}
                </Field>

                {/* Online presence */}
                <Field label="Online Presence" hint="Optional. Helps us match your brand voice across channels.">
                  <div className="grid grid-cols-1 gap-2">
                    <TextInput placeholder="Website URL" value={brand.website}
                      onChange={e => setBrand(p => ({ ...p, website: e.target.value }))} data-testid="onboard-website" />
                    <div className="grid grid-cols-2 gap-2">
                      <TextInput placeholder="Instagram handle" value={brand.instagram}
                        onChange={e => setBrand(p => ({ ...p, instagram: e.target.value }))} data-testid="onboard-instagram" />
                      <TextInput placeholder="TikTok handle" value={brand.tiktok}
                        onChange={e => setBrand(p => ({ ...p, tiktok: e.target.value }))} data-testid="onboard-tiktok" />
                    </div>
                  </div>
                </Field>

                <div className="pt-3 space-y-2">
                  <PrimaryBtn onClick={handleAnalyze} disabled={saving || analyzingVoice} testId="onboard-finish">
                    {(saving || analyzingVoice) ? <><ArrowsClockwise size={16} className="animate-spin" /> Setting up</> : <>Finish setup <ArrowRight size={16} weight="bold" /></>}
                  </PrimaryBtn>
                  <GhostBtn onClick={() => finishSetup(true)} testId="onboard-skip-C">
                    Skip for now &rarr;
                  </GhostBtn>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
